import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE_URL from "../config";
import "./BookMePublicPage.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DEFAULT_PERMISSION_DISCLAIMER =
  "By booking this meeting, I agree that the agent may contact me by phone, text, or email about my appointment and related insurance services.";

const BASIC_US_TIMEZONES = [
  {
    label: "Eastern Time",
    value: "America/New_York",
  },
  {
    label: "Central Time",
    value: "America/Chicago",
  },
  {
    label: "Mountain Time",
    value: "America/Denver",
  },
  {
    label: "Pacific Time",
    value: "America/Los_Angeles",
  },
];

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatDate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function parseDateString(dateString) {
  if (!dateString) return null;

  const [year, month, day] = String(dateString).split("-").map(Number);

  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

function buildDateRange() {
  const start = new Date();
  const end = new Date();

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  end.setDate(start.getDate() + 31);

  return {
    startDate: formatDate(start),
    endDate: formatDate(end),
  };
}

function getIsEmbedMode() {
  return new URLSearchParams(window.location.search).get("embed") === "true";
}

function getVisitorTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";
  } catch {
    return "America/Chicago";
  }
}

function getTimezoneOptions() {
  return BASIC_US_TIMEZONES;
}

function getDefaultUsTimezone(detectedTimezone) {
  const isSupported = BASIC_US_TIMEZONES.some(
    (item) => item.value === detectedTimezone
  );

  return isSupported ? detectedTimezone : "America/Chicago";
}

function formatFriendlyDate(dateString) {
  const date = parseDateString(dateString);

  if (!date) return dateString || "";

  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function formatShortDate(dateString) {
  const date = parseDateString(dateString);

  if (!date) return dateString || "";

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatTime12(rawTime) {
  if (!rawTime) return "";

  const match = String(rawTime).trim().match(/^(\d{1,2}):(\d{2})/);

  if (!match) return String(rawTime);

  let hour = Number(match[1]);
  const minute = match[2];

  if (!Number.isFinite(hour)) return String(rawTime);

  const suffix = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;

  return `${hour}:${minute} ${suffix}`;
}

function isValidTimezone(timezone) {
  if (!timezone || typeof timezone !== "string") return false;

  try {
    Intl.DateTimeFormat("en-US", { timeZone: timezone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function getTimezoneLabel(timezone) {
  const matchedTimezone = BASIC_US_TIMEZONES.find(
    (item) => item.value === timezone
  );

  return matchedTimezone?.label || "Central Time";
}

function getZonedParts(date, timezone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });

  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    if (part.type !== "literal") acc[part.type] = part.value;
    return acc;
  }, {});

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
}

function getTimezoneOffsetMs(date, timezone) {
  const parts = getZonedParts(date, timezone);

  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return asUtc - date.getTime();
}

function zonedTimeToUtc(parts, timezone) {
  if (!parts || !isValidTimezone(timezone)) return null;

  const utcGuess = new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second || 0
    )
  );

  const firstOffset = getTimezoneOffsetMs(utcGuess, timezone);
  const firstResult = new Date(utcGuess.getTime() - firstOffset);
  const secondOffset = getTimezoneOffsetMs(firstResult, timezone);

  if (firstOffset !== secondOffset) {
    return new Date(utcGuess.getTime() - secondOffset);
  }

  return firstResult;
}

function parseSlotLocalParts(slot) {
  const rawStart = String(slot?.startAt || slot?.start_at || "").trim();
  const normalizedStart = rawStart.replace(" ", "T");

  const startMatch = normalizedStart.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/
  );

  if (startMatch) {
    return {
      year: Number(startMatch[1]),
      month: Number(startMatch[2]),
      day: Number(startMatch[3]),
      hour: Number(startMatch[4]),
      minute: Number(startMatch[5]),
      second: Number(startMatch[6] || 0),
    };
  }

  const dateMatch = String(slot?.date || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = String(slot?.time || "00:00").match(/^(\d{1,2}):(\d{2})/);

  if (!dateMatch || !timeMatch) return null;

  return {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
    second: 0,
  };
}

function formatDateInTimezone(date, timezone) {
  if (!date || !isValidTimezone(timezone)) return "";

  const parts = getZonedParts(date, timezone);

  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
}

function formatTimeInTimezone(date, timezone) {
  if (!date || !isValidTimezone(timezone)) return "";

  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function normalizeSlotForDisplay(slot, profileTimezone, visitorTimezone) {
  const agentTimezone =
    slot?.timezone ||
    slot?.profileTimezone ||
    slot?.profile_timezone ||
    profileTimezone ||
    "UTC";

  const safeAgentTimezone = isValidTimezone(agentTimezone) ? agentTimezone : "UTC";
  const safeVisitorTimezone = isValidTimezone(visitorTimezone)
    ? visitorTimezone
    : "America/Chicago";

  let utcDate = null;

  if (slot?.startAtUtc || slot?.start_at_utc) {
    const parsedUtc = new Date(slot.startAtUtc || slot.start_at_utc);

    if (!Number.isNaN(parsedUtc.getTime())) {
      utcDate = parsedUtc;
    }
  }

  if (!utcDate) {
    const localParts = parseSlotLocalParts(slot);
    utcDate = zonedTimeToUtc(localParts, safeAgentTimezone);
  }

  const displayDate = utcDate
    ? formatDateInTimezone(utcDate, safeVisitorTimezone)
    : slot?.date;

  const displayTime = utcDate
    ? formatTimeInTimezone(utcDate, safeVisitorTimezone)
    : slot?.displayTime || slot?.display_time || formatTime12(slot?.time);

  return {
    ...slot,
    timezone: safeAgentTimezone,
    attendeeTimezone: safeVisitorTimezone,
    originalDate: slot?.originalDate || slot?.date,
    displayDate: displayDate || slot?.date,
    displayTime,
    sortTime: utcDate ? utcDate.getTime() : 0,
  };
}

function getSlotDisplayTime(slot) {
  return slot?.displayTime || slot?.display_time || formatTime12(slot?.time);
}

function getProfileLogoUrl(profile) {
  return (
    profile?.account_logo_url ||
    profile?.logo_url ||
    profile?.navbar_logo_url ||
    profile?.login_logo_url ||
    profile?.accountLogoUrl ||
    profile?.account_logo ||
    ""
  );
}

function AccountLogo({ profile, className = "" }) {
  const logoUrl = getProfileLogoUrl(profile);

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt="Account logo"
        className={`bookme-logo-image ${className}`.trim()}
      />
    );
  }

  return (
    <div className={`bookme-default-logo ${className}`.trim()} aria-label="Agent Book CRM">
      <span className="bookme-default-logo-mark">AB</span>
      <span className="bookme-default-logo-text">Agent Book CRM</span>
    </div>
  );
}

function getMonthStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getNextMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

function getPreviousMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() - 1, 1);
}

function buildCalendarDays(monthDate) {
  const firstDay = getMonthStart(monthDate);
  const firstGridDay = new Date(firstDay);

  firstGridDay.setDate(firstDay.getDate() - firstDay.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(firstGridDay);
    date.setDate(firstGridDay.getDate() + index);

    return {
      date,
      dateString: formatDate(date),
      isCurrentMonth: date.getMonth() === monthDate.getMonth(),
      dayNumber: date.getDate(),
    };
  });
}

const BookMePublicPage = () => {
  const { profileSlug } = useParams();
  const isEmbedMode = getIsEmbedMode();

  const detectedVisitorTimezone = useMemo(() => getVisitorTimezone(), []);
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const [selectedVisitorTimezone, setSelectedVisitorTimezone] = useState(
    getDefaultUsTimezone(detectedVisitorTimezone)
  );

  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [profile, setProfile] = useState(null);
  const [meetingTypes, setMeetingTypes] = useState([]);
  const [selectedMeetingTypeSlug, setSelectedMeetingTypeSlug] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(getMonthStart(new Date()));
  const [attendeeName, setAttendeeName] = useState("");
  const [attendeeEmail, setAttendeeEmail] = useState("");
  const [attendeePhone, setAttendeePhone] = useState("");
  const [notes, setNotes] = useState("");
  const [permissionAccepted, setPermissionAccepted] = useState(false);
  const [successAppointment, setSuccessAppointment] = useState(null);
  const [confirmedMeetingType, setConfirmedMeetingType] = useState(null);
  const [confirmedSlot, setConfirmedSlot] = useState(null);
  const [error, setError] = useState("");

  const selectedMeetingType = useMemo(
    () => meetingTypes.find((item) => item.slug === selectedMeetingTypeSlug),
    [meetingTypes, selectedMeetingTypeSlug]
  );

  const selectedPermissionDisclaimer =
    selectedMeetingType?.permission_disclaimer ||
    selectedMeetingType?.permissionDisclaimer ||
    DEFAULT_PERMISSION_DISCLAIMER;

  const groupedSlots = useMemo(() => {
    return slots.reduce((acc, slot) => {
      const dateKey = slot.displayDate || slot.date;

      if (!acc[dateKey]) acc[dateKey] = [];

      acc[dateKey].push(slot);
      acc[dateKey].sort((a, b) => (a.sortTime || 0) - (b.sortTime || 0));

      return acc;
    }, {});
  }, [slots]);

  const availableDates = useMemo(() => Object.keys(groupedSlots).sort(), [groupedSlots]);
  const availableDateSet = useMemo(() => new Set(availableDates), [availableDates]);

  const selectedDateSlots = useMemo(
    () => (selectedDate ? groupedSlots[selectedDate] || [] : []),
    [groupedSlots, selectedDate]
  );

  const calendarDays = useMemo(() => buildCalendarDays(calendarMonth), [calendarMonth]);

  const canGoToPreviousMonth = useMemo(() => {
    const todayMonth = getMonthStart(new Date());
    return calendarMonth > todayMonth;
  }, [calendarMonth]);

  const refreshSlotsForTimezone = useCallback(
    (timezone) => {
      const safeTimezone = isValidTimezone(timezone) ? timezone : "America/Chicago";

      setSlots((currentSlots) => {
        const updatedSlots = currentSlots.map((slot) =>
          normalizeSlotForDisplay(
            slot,
            slot?.timezone || profile?.timezone,
            safeTimezone
          )
        );

        if (updatedSlots.length > 0) {
          const updatedSelectedSlot = selectedSlot
            ? updatedSlots.find((slot) => slot.startAt === selectedSlot.startAt)
            : null;

          if (updatedSelectedSlot) {
            setSelectedSlot(updatedSelectedSlot);
            setSelectedDate(updatedSelectedSlot.displayDate || updatedSelectedSlot.date);
          } else {
            const firstSlotDate = updatedSlots[0].displayDate || updatedSlots[0].date;
            setSelectedDate(firstSlotDate);

            const parsedFirstSlotDate = parseDateString(firstSlotDate);
            if (parsedFirstSlotDate) {
              setCalendarMonth(getMonthStart(parsedFirstSlotDate));
            }
          }
        }

        return updatedSlots;
      });
    },
    [profile?.timezone, selectedSlot]
  );

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_BASE_URL}/api/public/bookme/${profileSlug}`);
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "This booking page is not available.");
      }

      setProfile(data.profile);
      setMeetingTypes(data.meetingTypes || []);

      if (data.meetingTypes?.length) {
        setSelectedMeetingTypeSlug(data.meetingTypes[0].slug);
      }
    } catch (err) {
      console.error("Load BookMe public profile error:", err);
      setError(err.message || "Failed to load booking page.");
    } finally {
      setLoading(false);
    }
  }, [profileSlug]);

  const loadSlots = useCallback(
    async (meetingTypeSlug) => {
      if (!meetingTypeSlug) return;

      try {
        setSlotsLoading(true);
        setError("");
        setSelectedDate("");
        setSelectedSlot(null);
        setPermissionAccepted(false);

        const { startDate, endDate } = buildDateRange();

        const res = await fetch(
          `${API_BASE_URL}/api/public/bookme/${profileSlug}/${meetingTypeSlug}/slots?startDate=${startDate}&endDate=${endDate}&visitorTimezone=${encodeURIComponent(selectedVisitorTimezone)}`
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || "Failed to load available times.");
        }

        const loadedSlots = (data.slots || []).map((slot) =>
          normalizeSlotForDisplay(
            slot,
            data?.meetingType?.profile_timezone || profile?.timezone,
            selectedVisitorTimezone
          )
        );

        setSlots(loadedSlots);

        if (loadedSlots.length > 0) {
          const firstSlotDate = loadedSlots[0].displayDate || loadedSlots[0].date;
          setSelectedDate(firstSlotDate);

          const parsedFirstSlotDate = parseDateString(firstSlotDate);
          if (parsedFirstSlotDate) {
            setCalendarMonth(getMonthStart(parsedFirstSlotDate));
          }
        }
      } catch (err) {
        console.error("Load BookMe slots error:", err);
        setError(err.message || "Failed to load available times.");
        setSlots([]);
        setSelectedDate("");
        setSelectedSlot(null);
        setPermissionAccepted(false);
      } finally {
        setSlotsLoading(false);
      }
    },
    [profileSlug, profile?.timezone, selectedVisitorTimezone]
  );

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (selectedMeetingTypeSlug) {
      loadSlots(selectedMeetingTypeSlug);
    }
  }, [selectedMeetingTypeSlug, loadSlots]);

  const handleMeetingTypeChange = (event) => {
    setSelectedMeetingTypeSlug(event.target.value);
    setSelectedDate("");
    setSelectedSlot(null);
    setPermissionAccepted(false);
  };

  const handleTimezoneChange = (event) => {
    const nextTimezone = event.target.value || "America/Chicago";

    setSelectedVisitorTimezone(nextTimezone);
    setPermissionAccepted(false);
    refreshSlotsForTimezone(nextTimezone);
  };

  const handleDateClick = (day) => {
    if (!day.isCurrentMonth || !availableDateSet.has(day.dateString)) return;

    setSelectedDate(day.dateString);
    setSelectedSlot(null);
    setPermissionAccepted(false);
  };

  const handleBook = async (event) => {
    event.preventDefault();

    if (!selectedMeetingTypeSlug) return setError("Please choose a meeting type.");
    if (!selectedSlot) return setError("Please choose a time.");
    if (!attendeeName.trim()) return setError("Please enter your name.");
    if (!attendeeEmail.trim()) return setError("Please enter your email.");

    if (!permissionAccepted) {
      return setError("Please accept the permission to contact disclosure before booking.");
    }

    try {
      setBooking(true);
      setError("");

      const res = await fetch(
        `${API_BASE_URL}/api/public/bookme/${profileSlug}/${selectedMeetingTypeSlug}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attendeeName,
            attendeeEmail,
            attendeePhone,
            startAt: selectedSlot.startAt,
            timezone: selectedSlot?.timezone || profile?.timezone || "UTC",
            attendeeTimezone: selectedVisitorTimezone,
            notes,
            permissionAccepted,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to book appointment.");
      }

      setConfirmedMeetingType(selectedMeetingType);
      setConfirmedSlot(selectedSlot);
      setSuccessAppointment(data.appointment);
      setSelectedSlot(null);
    } catch (err) {
      console.error("BookMe booking error:", err);
      setError(err.message || "Failed to book appointment.");
    } finally {
      setBooking(false);
    }
  };

  const pageClassName = `bookme-public-page ${isEmbedMode ? "bookme-public-page-embed" : ""}`;

  if (loading) {
    return (
      <div className={pageClassName}>
        <div className="bookme-public-loading-card">
          <div className="bookme-public-loader" />
          <h1>Book a Meeting</h1>
          <p>Loading booking page...</p>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className={pageClassName}>
        <div className="bookme-public-card bookme-public-error-card">
          <h1>Book a Meeting</h1>
          <p className="bookme-public-error">{error}</p>
        </div>
      </div>
    );
  }

  if (successAppointment) {
    return (
      <div className={pageClassName}>
        <div className="bookme-public-success-card">
          <div className="bookme-public-success-icon">✓</div>
          <p className="bookme-public-eyebrow">Confirmed</p>
          <h1>Your meeting is booked</h1>
          <p>
            You are scheduled with{" "}
            {profile?.display_name || profile?.user_name || "your agent"}.
          </p>

          <div className="bookme-public-confirm-box">
            <strong>
              {confirmedMeetingType?.name || successAppointment.title || "BookMe Appointment"}
            </strong>
            <span>
              {confirmedSlot?.displayDate ? formatFriendlyDate(confirmedSlot.displayDate) : ""}
              {confirmedSlot ? ` at ${getSlotDisplayTime(confirmedSlot)}` : ""}
            </span>
            <small>{getTimezoneLabel(selectedVisitorTimezone)}</small>
          </div>

          <p className="bookme-public-footer">Scheduled through BookMe</p>
        </div>
      </div>
    );
  }

  return (
    <div className={pageClassName}>
      <div className="bookme-layout">
        {!isEmbedMode && (
          <header className="bookme-site-header">
            <div className="bookme-site-brand">
              <div className="bookme-site-logo">
                <AccountLogo profile={profile} />
              </div>
            </div>

            <div className="bookme-site-title">
              <p className="bookme-public-eyebrow">BookMe</p>
              <h1>Schedule a Meeting</h1>
            </div>

            <div className="bookme-site-header-spacer" />
          </header>
        )}

        <div className="bookme-booking-shell">
          <aside className="bookme-host-panel">
            <p className="bookme-public-eyebrow">Scheduling with</p>
            <h2>{profile?.display_name || profile?.user_name}</h2>
            <p className="bookme-host-headline">
              {profile?.headline || "Schedule a meeting with me"}
            </p>

            {profile?.description ? (
              <p className="bookme-host-description">{profile.description}</p>
            ) : null}

            <div className="bookme-summary-card">
              <span>Meeting type</span>
              <strong>{selectedMeetingType?.name || "Choose a meeting"}</strong>
              {selectedMeetingType?.duration_minutes ? (
                <small>{selectedMeetingType.duration_minutes} minutes</small>
              ) : null}
            </div>

            {selectedMeetingType?.description ? (
              <div className="bookme-summary-card">
                <span>About this meeting</span>
                <small>{selectedMeetingType.description}</small>
              </div>
            ) : null}
          </aside>

          <main className="bookme-scheduler-panel">
            <section className="bookme-step-section">
              <div className="bookme-step-header">
                <div>
                  <p className="bookme-step-label">Meeting</p>
                  <h3>What would you like to schedule?</h3>
                </div>
              </div>

              {meetingTypes.length === 0 ? (
                <p className="bookme-public-muted">
                  No meeting types are available right now.
                </p>
              ) : (
                <select
                  className="bookme-meeting-select"
                  value={selectedMeetingTypeSlug}
                  onChange={handleMeetingTypeChange}
                >
                  {meetingTypes.map((meetingType) => (
                    <option key={meetingType.id} value={meetingType.slug}>
                      {meetingType.name} · {meetingType.duration_minutes} minutes
                    </option>
                  ))}
                </select>
              )}
            </section>

            <section className="bookme-calendar-time-grid">
              <div className="bookme-calendar-panel">
                <div className="bookme-calendar-head">
                  <button
                    type="button"
                    className="bookme-month-nav"
                    onClick={() => setCalendarMonth((prev) => getPreviousMonth(prev))}
                    disabled={!canGoToPreviousMonth}
                    aria-label="Previous month"
                  >
                    ‹
                  </button>

                  <div>
                    <p className="bookme-step-label">Date</p>
                    <h3>
                      {MONTH_NAMES[calendarMonth.getMonth()]} {calendarMonth.getFullYear()}
                    </h3>
                  </div>

                  <button
                    type="button"
                    className="bookme-month-nav"
                    onClick={() => setCalendarMonth((prev) => getNextMonth(prev))}
                    aria-label="Next month"
                  >
                    ›
                  </button>
                </div>

                {slotsLoading ? (
                  <div className="bookme-state-box">
                    <div className="bookme-public-loader" />
                    <p>Loading available dates...</p>
                  </div>
                ) : availableDates.length === 0 ? (
                  <div className="bookme-state-box">
                    <p>No available dates found for the next 31 days.</p>
                  </div>
                ) : (
                  <>
                    <div className="bookme-calendar-weekdays">
                      {WEEKDAY_LABELS.map((label) => (
                        <span key={label}>{label}</span>
                      ))}
                    </div>

                    <div className="bookme-calendar-grid" role="grid">
                      {calendarDays.map((day) => {
                        const isAvailable = availableDateSet.has(day.dateString);
                        const isSelected = selectedDate === day.dateString;
                        const isToday = day.dateString === formatDate(new Date());

                        return (
                          <button
                            key={day.dateString}
                            type="button"
                            className={[
                              "bookme-calendar-day",
                              !day.isCurrentMonth ? "muted" : "",
                              isAvailable ? "available" : "",
                              isSelected ? "selected" : "",
                              isToday ? "today" : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                            disabled={!day.isCurrentMonth || !isAvailable}
                            onClick={() => handleDateClick(day)}
                          >
                            <span>{day.dayNumber}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <div className="bookme-time-panel">
                <div className="bookme-step-header">
                  <div>
                    <p className="bookme-step-label">Time</p>
                    <h3>{selectedDate ? formatFriendlyDate(selectedDate) : "Choose a date"}</h3>
                  </div>
                </div>

                {!selectedDate ? (
                  <div className="bookme-state-box">
                    <p>Select an available date on the calendar.</p>
                  </div>
                ) : selectedDateSlots.length === 0 ? (
                  <div className="bookme-state-box">
                    <p>No times are available for this date.</p>
                  </div>
                ) : (
                  <div className="bookme-time-list">
                    {selectedDateSlots.map((slot) => (
                      <button
                        key={slot.startAt}
                        type="button"
                        className={`bookme-time-button ${
                          selectedSlot?.startAt === slot.startAt ? "active" : ""
                        }`}
                        onClick={() => {
                          setSelectedSlot(slot);
                          setPermissionAccepted(false);
                        }}
                      >
                        {getSlotDisplayTime(slot)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </section>

            <section className={`bookme-form-panel ${selectedSlot ? "active" : ""}`}>
              <div className="bookme-step-header">
                <div>
                  <p className="bookme-step-label">Confirm</p>
                  <h3>Your information</h3>
                </div>
              </div>

              {selectedSlot ? (
                <>
                  <div className="bookme-selected-box">
                    <span>Selected appointment</span>
                    <strong>
                      {selectedMeetingType?.name || "Meeting"} ·{" "}
                      {formatShortDate(selectedSlot.displayDate || selectedSlot.date)} at{" "}
                      {getSlotDisplayTime(selectedSlot)}
                    </strong>
                  </div>

                  <label className="bookme-timezone-control">
                    <span>Timezone</span>
                    <select value={selectedVisitorTimezone} onChange={handleTimezoneChange}>
                      {timezoneOptions.map((timezone) => (
                        <option key={timezone.value} value={timezone.value}>
                          {timezone.label}
                        </option>
                      ))}
                    </select>
                    <small>
                      Auto-detected when possible. Change this if the appointment time
                      should be shown in a different US timezone.
                    </small>
                  </label>
                </>
              ) : (
                <div className="bookme-state-box compact">
                  <p>Choose a time to continue.</p>
                </div>
              )}

              {error ? <p className="bookme-public-error">{error}</p> : null}

              {selectedSlot && (
                <form className="bookme-public-form" onSubmit={handleBook}>
                  <div className="bookme-form-grid">
                    <label>
                      Name
                      <input
                        value={attendeeName}
                        onChange={(e) => setAttendeeName(e.target.value)}
                        placeholder="Your full name"
                      />
                    </label>

                    <label>
                      Email
                      <input
                        type="email"
                        value={attendeeEmail}
                        onChange={(e) => setAttendeeEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </label>
                  </div>

                  <label>
                    Phone
                    <input
                      value={attendeePhone}
                      onChange={(e) => setAttendeePhone(e.target.value)}
                      placeholder="Optional"
                    />
                  </label>

                  <label>
                    Notes
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Optional"
                      rows={3}
                    />
                  </label>

                  <div className="bookme-permission-box">
                    <label className="bookme-permission-label">
                      <input
                        type="checkbox"
                        checked={permissionAccepted}
                        onChange={(e) => setPermissionAccepted(e.target.checked)}
                      />
                      <span>
                        <strong>Permission to contact</strong>
                        <small>{selectedPermissionDisclaimer}</small>
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="bookme-public-submit"
                    disabled={booking || !selectedSlot || !permissionAccepted}
                  >
                    {booking ? "Booking..." : "Book Meeting"}
                  </button>

                  <p className="bookme-public-footer">Scheduled through BookMe</p>
                </form>
              )}
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default BookMePublicPage;